import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonDto, RelationshipDto } from '../services/api';
import { normalizeRelationshipType, getPersonDisplayName } from './relationshipLabels';

/**
 * React hook that provides i18n-aware versions of all relationship label functions.
 * Replaces the static `relationshipLabels.ts` functions wherever the result is
 * shown to the user (edge labels, family link lists, read-only views, previews).
 */
export const useRelationshipLabels = () => {
  const { t } = useTranslation();

  const getParentLabelForGender = useCallback(
    (gender?: string): string => {
      const g = gender?.trim().toLowerCase();
      if (g === 'male') return t('relationship.types.father', { ns: 'canvas' });
      if (g === 'female') return t('relationship.types.mother', { ns: 'canvas' });
      return t('relationship.types.parent', { ns: 'canvas' });
    },
    [t]
  );

  const toDisplayRelationshipLabel = useCallback(
    (relationshipType?: string): string => {
      const normalized = normalizeRelationshipType(relationshipType);
      switch (normalized) {
        case 'parent':      return t('relationship.types.parent', { ns: 'canvas' });
        case 'stepfather':  return t('relationship.types.stepFather', { ns: 'canvas' });
        case 'stepmother':  return t('relationship.types.stepMother', { ns: 'canvas' });
        case 'stepchild':   return t('relationship.types.stepChild', { ns: 'canvas' });
        case 'child':       return t('relationship.types.child', { ns: 'canvas' });
        case 'grandparent': return t('relationship.types.grandparent', { ns: 'canvas' });
        case 'grandchild':  return t('relationship.types.grandchild', { ns: 'canvas' });
        case 'spouse':      return t('relationship.types.spouse', { ns: 'canvas' });
        case 'sibling':     return t('relationship.types.sibling', { ns: 'canvas' });
        case 'stepsibling': return t('relationship.types.stepSibling', { ns: 'canvas' });
        case 'aunt':        return t('relationship.types.aunt', { ns: 'canvas' });
        case 'uncle':       return t('relationship.types.uncle', { ns: 'canvas' });
        case 'niecenephew': return t('relationship.types.nieceNephew', { ns: 'canvas' });
        case 'cousin':      return t('relationship.types.cousin', { ns: 'canvas' });
        case 'inlaw':       return t('relationship.types.inLaw', { ns: 'canvas' });
        case 'other':       return t('relationship.types.other', { ns: 'canvas' });
        default:
          return normalized
            ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
            : t('relationship.types.other', { ns: 'canvas' });
      }
    },
    [t]
  );

  const getDisplayRelationshipLabelForEdge = useCallback(
    (
      relationshipType: string | undefined,
      personB?: Pick<PersonDto, 'gender'> | null
    ): string => {
      const normalized = normalizeRelationshipType(relationshipType);
      if (normalized === 'parent') return getParentLabelForGender(personB?.gender);
      return toDisplayRelationshipLabel(relationshipType);
    },
    [getParentLabelForGender, toDisplayRelationshipLabel]
  );

  const getRelativeLabelForPerson = useCallback(
    (
      relationship: RelationshipDto,
      currentPersonId: string,
      relatedPerson?: Pick<PersonDto, 'gender'> | null
    ): string => {
      const normalized = normalizeRelationshipType(relationship.type);

      if (relationship.personAId === currentPersonId) {
        if (normalized === 'parent') return getParentLabelForGender(relatedPerson?.gender);
        return toDisplayRelationshipLabel(relationship.type);
      }

      // Inverse direction labels
      const inverseMap: Record<string, string> = {
        parent:      t('relationship.types.child', { ns: 'canvas' }),
        stepfather:  t('relationship.types.stepChild', { ns: 'canvas' }),
        stepmother:  t('relationship.types.stepChild', { ns: 'canvas' }),
        child:       getParentLabelForGender(relatedPerson?.gender),
        grandparent: t('relationship.types.grandchild', { ns: 'canvas' }),
        grandchild:  t('relationship.types.grandparent', { ns: 'canvas' }),
        aunt:        t('relationship.types.nieceNephew', { ns: 'canvas' }),
        uncle:       t('relationship.types.nieceNephew', { ns: 'canvas' }),
        spouse:      t('relationship.types.spouse', { ns: 'canvas' }),
        sibling:     t('relationship.types.sibling', { ns: 'canvas' }),
        stepsibling: t('relationship.types.stepSibling', { ns: 'canvas' }),
        cousin:      t('relationship.types.cousin', { ns: 'canvas' }),
        inlaw:       t('relationship.types.inLaw', { ns: 'canvas' }),
        other:       t('relationship.types.other', { ns: 'canvas' }),
      };

      return inverseMap[normalized] ?? toDisplayRelationshipLabel(relationship.type);
    },
    [t, getParentLabelForGender, toDisplayRelationshipLabel]
  );

  const getCanvasEdgeSemantics = useCallback(
    (
      relationshipType: string | undefined,
      personB?: Pick<PersonDto, 'gender'> | null
    ) => {
      const normalized = normalizeRelationshipType(relationshipType);

      if (normalized === 'sibling') {
        return {
          label: t('relationship.types.sibling', { ns: 'canvas' }),
          sourceArrowShape: 'none',
          targetArrowShape: 'none',
          lineStyle: 'solid',
        } as const;
      }

      if (normalized === 'stepsibling') {
        return {
          label: t('relationship.types.stepSibling', { ns: 'canvas' }),
          sourceArrowShape: 'none',
          targetArrowShape: 'none',
          lineStyle: 'dashed',
        } as const;
      }

      if (normalized === 'spouse') {
        return {
          label: getDisplayRelationshipLabelForEdge(relationshipType, personB),
          sourceArrowShape: 'triangle',
          targetArrowShape: 'triangle',
          lineStyle: 'solid',
        } as const;
      }

      return {
        label: getDisplayRelationshipLabelForEdge(relationshipType, personB),
        sourceArrowShape: 'none',
        targetArrowShape: 'triangle',
        lineStyle: 'solid',
      } as const;
    },
    [t, getDisplayRelationshipLabelForEdge]
  );

  const getRelationshipPreview = useCallback(
    (
      personA: Pick<PersonDto, 'firstName' | 'lastName' | 'fullName'> | null | undefined,
      relationshipType: string,
      personB: Pick<PersonDto, 'firstName' | 'lastName' | 'fullName' | 'gender'> | null | undefined
    ): string => {
      const personAName = getPersonDisplayName(personA);
      const personBName = getPersonDisplayName(personB);
      const normalized = normalizeRelationshipType(relationshipType);
      const typeLabel = toDisplayRelationshipLabel(relationshipType).toLowerCase();

      // Cases where personB is the actor (B is TYPE of A)
      const bIsTypeOfACases = new Set([
        'parent', 'stepfather', 'stepmother', 'child',
        'grandparent', 'grandchild', 'aunt', 'uncle',
      ]);

      if (normalized === 'parent') {
        return t('relationship.preview.bIsTypeOfA', {
          ns: 'canvas',
          personA: personAName,
          personB: personBName,
          type: getParentLabelForGender(personB?.gender).toLowerCase(),
        });
      }

      if (bIsTypeOfACases.has(normalized)) {
        return t('relationship.preview.bIsTypeOfA', {
          ns: 'canvas',
          personA: personAName,
          personB: personBName,
          type: typeLabel,
        });
      }

      return t('relationship.preview.aIsTypeOfB', {
        ns: 'canvas',
        personA: personAName,
        personB: personBName,
        type: typeLabel,
      });
    },
    [t, toDisplayRelationshipLabel, getParentLabelForGender]
  );

  return {
    toDisplayRelationshipLabel,
    getParentLabelForGender,
    getDisplayRelationshipLabelForEdge,
    getRelativeLabelForPerson,
    getCanvasEdgeSemantics,
    getRelationshipPreview,
  };
};
